// Only one showroom modal is open at a time; opening one closes the others.

const openModals = new Set();

class ShowroomModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("[data-showroom-modal-dialog]");

    this.handleKeyUp = (event) => {
      if (event.key === "Escape" && this.isOpen) this.close();
    };

    this.addEventListener("click", (event) => {
      if (event.target.closest("[data-showroom-modal-close]")) {
        this.close();
        return;
      }

      // A click on the backdrop (outside the dialog) closes it. Targets that
      // removed themselves while handling the click (e.g. a color chip's
      // remove button) are no longer in the dialog but weren't outside it.
      if (this.dialog && event.target.isConnected && !this.dialog.contains(event.target)) this.close();
    });

    document.addEventListener("keyup", this.handleKeyUp);
  }

  disconnectedCallback() {
    document.removeEventListener("keyup", this.handleKeyUp);
    if (this.isOpen) this.close();
  }

  get isOpen() {
    return this.hasAttribute("open");
  }

  open(trigger = document.activeElement) {
    if (this.isOpen) return;

    openModals.forEach((modal) => modal !== this && modal.close({ restoreFocus: false }));

    this.returnFocusEl = trigger;
    this.hidden = false;
    openModals.add(this);

    window.requestAnimationFrame(() => {
      this.setAttribute("open", "");
      window.openFullDimmer?.(this);
      window.lockScroll?.(this);
      window.trapFocus?.(this, this.querySelector("[autofocus]") || this.querySelector("[data-showroom-modal-close]"));
      this.dispatchEvent(new CustomEvent("showroom-modal:open", { bubbles: true }));
    });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;

    this.removeAttribute("open");
    openModals.delete(this);
    window.closeFullDimmer?.(this);
    window.unlockScroll?.(this);
    window.removeTrapFocus?.(restoreFocus ? this.returnFocusEl : null);

    const finish = () => {
      if (!this.isOpen) this.hidden = true;
    };

    // Fall back to a timer in case the modal has no transition to wait on.
    this.addEventListener("transitionend", finish, { once: true });
    window.setTimeout(finish, 400);

    this.dispatchEvent(new CustomEvent("showroom-modal:close", { bubbles: true }));
  }
}

if (!customElements.get("showroom-modal")) {
  customElements.define("showroom-modal", ShowroomModal);

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-showroom-modal-open]");
    if (!trigger) return;

    const modal = document.getElementById(trigger.dataset.showroomModalOpen);
    if (!modal?.open) return;

    event.preventDefault();
    modal.open(trigger);
  });
}

export const getModal = (id) => document.getElementById(id);
